/**
 * Settlement Nudge Service
 * 
 * Manages settlement nudges with rate limiting (once per day per person)
 */

import { 
  collection, 
  doc, 
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { Platform } from 'react-native';
import { db } from '@/lib/firebase';
import { logAnalyticsEvent } from './analyticsLogger';

export type SettlementNudge = {
  id: string;
  tripId: string;
  fromUserId: string;
  toUserId: string;
  amount: number;
  currency: string;
  lastNudgedAt: Date;
  createdAt: Date;
};

export type SettlementConfirmation = {
  id: string;
  tripId: string;
  fromUserId: string;
  toUserId: string;
  amount: number;
  currency: string;
  confirmedBy: string;
  status: 'confirmed';
  confirmedAt: Date | null;
  updatedAt: Date | null;
};

const isPermissionDeniedError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { code?: unknown; message?: unknown };
  if (
    candidate.code === 'permission-denied' ||
    candidate.code === 'missing-or-insufficient-permissions' ||
    candidate.code === 'functions/permission-denied'
  ) {
    return true;
  }

  return (
    typeof candidate.message === 'string' &&
    candidate.message.toLowerCase().includes('insufficient permissions')
  );
};

const getSettlementConfirmationDocId = (fromUserId: string, toUserId: string) =>
  `${fromUserId}_${toUserId}`;

/**
 * Check if a nudge can be sent (rate limit: once per day)
 */
export const canSendNudge = async (
  tripId: string,
  fromUserId: string,
  toUserId: string
): Promise<boolean> => {
  try {
    const nudgeId = `${tripId}_${fromUserId}_${toUserId}`;
    const nudgeRef = doc(db, 'settlementNudges', nudgeId);
    const nudgeDoc = await getDoc(nudgeRef);
    
    if (!nudgeDoc.exists()) {
      return true; // No previous nudge, can send
    }
    
    const data = nudgeDoc.data();
    const lastNudgedAt = data.lastNudgedAt?.toDate?.() || new Date(data.lastNudgedAt);
    const now = new Date();
    const hoursSinceLastNudge = (now.getTime() - lastNudgedAt.getTime()) / (1000 * 60 * 60);
    
    // Allow if 24 hours have passed
    return hoursSinceLastNudge >= 24;
  } catch (error) {
    console.error('[Settlements] Error checking nudge rate limit:', error);
    return false; // Fail safe: don't allow if error
  }
};

/**
 * Record that a nudge was sent
 * 
 * This creates a document in Firestore which triggers the onSettlementNudgeCreated
 * Cloud Function to send an email notification
 */
export const recordNudgeSent = async (
  tripId: string,
  fromUserId: string,
  toUserId: string,
  amount: number,
  currency: string
): Promise<void> => {
  try {
    // Use a unique ID for each nudge (allows multiple nudges over time)
    const nudgeId = `${tripId}_${fromUserId}_${toUserId}_${Date.now()}`;
    const nudgeRef = doc(db, 'settlementNudges', nudgeId);
    
    // Create a new document (not merge) to trigger the Cloud Function
    await setDoc(nudgeRef, {
      tripId,
      fromUserId,
      toUserId,
      amount,
      currency,
      lastNudgedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    });

    // Also update the rate limit tracking document
    const rateLimitId = `${tripId}_${fromUserId}_${toUserId}`;
    const rateLimitRef = doc(db, 'settlementNudges', rateLimitId);
    await setDoc(rateLimitRef, {
      tripId,
      fromUserId,
      toUserId,
      lastNudgedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    console.error('[Settlements] Error recording nudge:', error);
    throw error;
  }
};

/**
 * Get the last nudge time for a settlement
 */
export const getLastNudgeTime = async (
  tripId: string,
  fromUserId: string,
  toUserId: string
): Promise<Date | null> => {
  try {
    const nudgeId = `${tripId}_${fromUserId}_${toUserId}`;
    const nudgeRef = doc(db, 'settlementNudges', nudgeId);
    const nudgeDoc = await getDoc(nudgeRef);
    
    if (!nudgeDoc.exists()) {
      return null;
    }
    
    const data = nudgeDoc.data();
    const lastNudgedAt = data.lastNudgedAt?.toDate?.() || new Date(data.lastNudgedAt);
    return lastNudgedAt;
  } catch (error) {
    console.error('[Settlements] Error getting last nudge time:', error);
    return null;
  }
};

/**
 * Mark a settlement as confirmed/paid by the person who should receive money.
 */
export const confirmSettlementPaid = async (
  tripId: string,
  fromUserId: string,
  toUserId: string,
  amount: number,
  currency: string,
  confirmedBy: string
): Promise<void> => {
  const docId = getSettlementConfirmationDocId(fromUserId, toUserId);
  const ref = doc(db, 'trips', tripId, 'settlementConfirmations', docId);

  await setDoc(ref, {
    tripId,
    fromUserId,
    toUserId,
    amount,
    currency,
    confirmedBy,
    status: 'confirmed',
    confirmedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });

  void logAnalyticsEvent('expense_settled', {
    tripId,
    actorId: confirmedBy,
    fromUserId,
    toUserId,
    amount,
    currency,
    result: 'success',
  });
};

/**
 * Remove settlement confirmation (re-open unpaid state).
 */
export const clearSettlementConfirmation = async (
  tripId: string,
  fromUserId: string,
  toUserId: string
): Promise<void> => {
  const docId = getSettlementConfirmationDocId(fromUserId, toUserId);
  const ref = doc(db, 'trips', tripId, 'settlementConfirmations', docId);
  await deleteDoc(ref);
};

const NATIVE_SETTLEMENT_CONFIRMATIONS_POLL_MS = 30000;

const mapSettlementConfirmationDoc = (
  tripId: string,
  id: string,
  data: Record<string, unknown>,
): SettlementConfirmation => {
  const confirmedAtRaw = data.confirmedAt as { toDate?: () => Date } | string | undefined;
  const updatedAtRaw = data.updatedAt as { toDate?: () => Date } | string | undefined;
  const confirmedAt =
    confirmedAtRaw &&
    typeof confirmedAtRaw === 'object' &&
    'toDate' in confirmedAtRaw &&
    confirmedAtRaw.toDate?.() instanceof Date
      ? confirmedAtRaw.toDate!()
      : confirmedAtRaw
        ? new Date(confirmedAtRaw as string)
        : null;
  const updatedAt =
    updatedAtRaw &&
    typeof updatedAtRaw === 'object' &&
    'toDate' in updatedAtRaw &&
    updatedAtRaw.toDate?.() instanceof Date
      ? updatedAtRaw.toDate!()
      : updatedAtRaw
        ? new Date(updatedAtRaw as string)
        : null;

  return {
    id,
    tripId: String(data.tripId || tripId),
    fromUserId: String(data.fromUserId || ''),
    toUserId: String(data.toUserId || ''),
    amount: Number(data.amount || 0),
    currency: String(data.currency || 'USD'),
    confirmedBy: String(data.confirmedBy || ''),
    status: 'confirmed' as const,
    confirmedAt,
    updatedAt,
  };
};

export const fetchSettlementConfirmations = async (
  tripId: string,
): Promise<SettlementConfirmation[]> => {
  const ref = collection(db, 'trips', tripId, 'settlementConfirmations');
  const snapshot = await getDocs(ref);
  return snapshot.docs.map((d) =>
    mapSettlementConfirmationDoc(tripId, d.id, d.data() as Record<string, unknown>),
  );
};

/**
 * Subscribe to settlement confirmations for a trip.
 */
export const subscribeSettlementConfirmations = (
  tripId: string,
  callback: (confirmations: SettlementConfirmation[]) => void
): (() => void) => {
  if (Platform.OS !== 'web') {
    let isCleanedUp = false;
    const poll = async () => {
      try {
        const confirmations = await fetchSettlementConfirmations(tripId);
        if (!isCleanedUp) {
          callback(confirmations);
        }
      } catch (error) {
        console.error('[Settlements] Native polling fetch failed:', error);
        if (!isCleanedUp) {
          callback([]);
        }
      }
    };
    void poll();
    const interval = setInterval(() => {
      void poll();
    }, NATIVE_SETTLEMENT_CONFIRMATIONS_POLL_MS);
    return () => {
      isCleanedUp = true;
      clearInterval(interval);
    };
  }

  const ref = collection(db, 'trips', tripId, 'settlementConfirmations');
  let latestConfirmations: SettlementConfirmation[] = [];
  let isCleanedUp = false;
  let unsubscribeFn: (() => void) | null = null;

  try {
    unsubscribeFn = onSnapshot(ref, (snapshot) => {
      if (isCleanedUp) {
        return;
      }

      setTimeout(() => {
        if (isCleanedUp) {
          return;
        }
        try {
          const confirmations = snapshot.docs.map((d) =>
            mapSettlementConfirmationDoc(tripId, d.id, d.data() as Record<string, unknown>),
          );
          latestConfirmations = confirmations;
          callback(confirmations);
        } catch (error) {
          console.error('[Settlements] Error processing snapshot:', error);
          if (!isCleanedUp) {
            callback(latestConfirmations);
          }
        }
      }, 0);
    }, (error) => {
      if (isCleanedUp) {
        return;
      }

      if (isPermissionDeniedError(error)) {
        callback(latestConfirmations);
        return;
      }

      console.error('[Settlements] Error subscribing to confirmations:', error);
      callback([]);
    });
  } catch (error) {
    console.error('[Settlements] Error setting up confirmations subscription:', error);
    callback([]);
  }

  return () => {
    isCleanedUp = true;
    if (unsubscribeFn) {
      try {
        unsubscribeFn();
      } catch {
        // ignore cleanup errors
      }
      unsubscribeFn = null;
    }
  };
};

